import React, {
  useCallback, useEffect, useRef, useState,
} from "react";

import { ArrowRightOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import {
  Button, Input, Select, Table,
} from "antd";
import moment from "moment";
import { toast } from "react-toastify";

import DatePicker from "../../Common/DatePicker/DatePicker";
import Loading from "../../Common/Loading";
import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
  selectSearchSuffixIcon,
} from "../../Common/UIComponents/SelectPopupScrollbar";
import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../Common/UIComponents/StyledTooltip";
import PreviewInvoice from "./PreviewInvoice";
import SetInvoiceDetails from "./SetInvoiceDetails";

import { formatInvoiceAmount } from "./utils";

import { listInvoiceSubmissionSubmittedBy, listInvoiceSubmissions } from "../../../actions/invoice";

import { ReactComponent as CalendarLineIcon } from "../../../assets/images/subscriptions/calendar-line.svg";
import { ReactComponent as SearchLineIcon } from "../../../assets/images/subscriptions/search-line.svg";

import "./CreateSubmissionInvoice.css";

const STEPS = ["Select submissions", "Set invoice details", "Preview & save"];
const PAGE_SIZE = 25;
const MAX_SUBMISSIONS_PER_INVOICE = 10;

const formatDate = (date) => {
  if (!date) return "";
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("MMM DD, YYYY") : "";
};

const formatTestDetails = (lines = []) => (
  lines
    .map((line) => [
      line.usage_category_name,
      line.amount != null && line.amount !== "" ? `$${line.amount}` : "",
    ].filter((part) => part != null && part !== "").join(" "))
    .join(" + ")
);

const mapSubmission = (submission) => ({
  id: submission.submission_id,
  name: submission.submission_name,
  po: submission.po,
  date: submission.submission_date,
  submittedBy: submission.submitted_by,
  samples: submission.num_samples,
  tests: formatTestDetails(submission.lines || []),
  amount: submission.amount,
  currency: submission.currency,
});

export default function CreateSubmissionInvoice({ onBack, onSaved = onBack }) {
  const [search, setSearch] = useState("");
  const [submittedBy, setSubmittedBy] = useState("");
  const [submitters, setSubmitters] = useState([]);
  const [dateSelected, setDateSelected] = useState("");
  const [prevDateSelected, setPrevDateSelected] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pageLoadFailed, setPageLoadFailed] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowsById, setSelectedRowsById] = useState({});
  const [step, setStep] = useState("select");
  const [detailsOpened, setDetailsOpened] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState(null);
  const invoiceDraftRef = useRef(null);
  invoiceDraftRef.current = invoiceDraft;
  const scrollbarRef = useRef(null);
  const fillLockRef = useRef(false);
  const wasLoadingMoreRef = useRef(false);
  const appliedSearchRef = useRef("");

  useEffect(() => () => {
    if (invoiceDraftRef.current?.previewUrl) {
      URL.revokeObjectURL(invoiceDraftRef.current.previewUrl);
    }
  }, []);

  const resetTable = useCallback(() => {
    setPageLoadFailed(false);
    setHasMore(true);
    setLoading(true);
    setLoadingMore(false);
    setRows([]);
    setPage(1);
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollToTop();
    }
  }, []);

  const applySearch = useCallback((value) => {
    const nextSearch = value.trim();
    if (appliedSearchRef.current === nextSearch) return;

    appliedSearchRef.current = nextSearch;
    setDebouncedSearch(nextSearch);
    resetTable();
  }, [resetTable]);

  useEffect(() => {
    const timer = setTimeout(() => applySearch(search), 350);

    return () => clearTimeout(timer);
  }, [applySearch, search]);

  useEffect(() => {
    if (loadingMore && scrollbarRef.current) {
      scrollbarRef.current.scrollToBottom();
    }
  }, [loadingMore]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;
    const isFirstPage = page === 1;

    const loadSubmissions = async () => {
      setPageLoadFailed(false);
      if (isFirstPage) {
        setLoading(true);
        setLoadingMore(false);
        if (scrollbarRef.current) {
          scrollbarRef.current.scrollToTop();
        }
      } else {
        setLoadingMore(true);
      }

      const response = await listInvoiceSubmissions({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        submittedBy: submittedBy || undefined,
        search: debouncedSearch || undefined,
        page,
        pageSize: PAGE_SIZE,
        signal: controller.signal,
      });

      if (!isActive || response.canceled) return;

      if (!response.success) {
        toast.error(response.message || "Failed to load submissions");
        if (isFirstPage) {
          setRows([]);
          setHasMore(false);
        } else {
          setPageLoadFailed(true);
          if (scrollbarRef.current) {
            scrollbarRef.current.scrollTop -= 100;
          }
        }
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const nextRows = response.submissions.map(mapSubmission);
      setRows((prev) => (isFirstPage ? nextRows : [...prev, ...nextRows]));
      setHasMore(nextRows.length > 0 && page * PAGE_SIZE < response.count);

      if (!isFirstPage && scrollbarRef.current) {
        scrollbarRef.current.scrollTop -= 4;
      }

      setLoading(false);
      setLoadingMore(false);
    };

    loadSubmissions();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [dateFrom, dateTo, debouncedSearch, listVersion, page, submittedBy]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const loadSubmitters = async () => {
      const response = await listInvoiceSubmissionSubmittedBy(controller.signal);
      if (!isActive || response.canceled) return;
      if (!response.success) {
        toast.error(response.message || "Failed to load submitted by");
        return;
      }
      setSubmitters(response.names);
    };

    loadSubmitters();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  /**
   * On scroll stop, if the scrollbar is at the bottom, load the next page.
   * @param {Object} event on scroll stop values from StyledScrollbar
   */
  const handleScrollToBottom = (event) => {
    if (!event || loading || loadingMore || !hasMore) return;

    const tolerance = 3;
    const bottom = Math.abs(
      Math.ceil(event.contentScrollHeight - event.scrollTop) - Math.ceil(event.clientHeight),
    ) <= tolerance;

    if (!bottom) return;

    if (pageLoadFailed) {
      setListVersion((version) => version + 1);
      return;
    }

    setPage((current) => current + 1);
  };

  useEffect(() => {
    if (wasLoadingMoreRef.current && !loading && !loadingMore) {
      fillLockRef.current = false;
    }
    wasLoadingMoreRef.current = loadingMore;
  }, [loading, loadingMore]);

  useEffect(() => {
    if (loading || loadingMore || !hasMore || pageLoadFailed || rows.length === 0) {
      return undefined;
    }

    let cancelled = false;
    const loadIfShort = () => {
      if (cancelled || fillLockRef.current) return;
      const scrollbar = scrollbarRef.current;
      if (!scrollbar || typeof scrollbar.getScrollState !== "function") return;

      const { clientHeight, scrollYPossible } = scrollbar.getScrollState(true);
      if (clientHeight <= 0 || scrollYPossible) return;

      fillLockRef.current = true;
      setPage((current) => current + 1);
    };

    const frame = requestAnimationFrame(loadIfShort);
    const holder = scrollbarRef.current?.holderElement;
    const observer = typeof ResizeObserver !== "undefined" && holder
      ? new ResizeObserver(loadIfShort)
      : null;
    if (observer) observer.observe(holder);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, pageLoadFailed, rows.length]);

  const columns = [
    {
      title: "Submission Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
      ellipsis: true,
    },
    {
      title: "PO Number",
      dataIndex: "po",
      key: "po",
      width: "12%",
    },
    {
      title: "Submission Date",
      dataIndex: "date",
      key: "date",
      width: "14%",
      render: (date) => formatDate(date),
    },
    {
      title: "Submitted By",
      dataIndex: "submittedBy",
      key: "submittedBy",
      width: "14%",
      ellipsis: true,
    },
    {
      title: "Samples",
      dataIndex: "samples",
      key: "samples",
      width: "9%",
    },
    {
      title: "Test Details",
      dataIndex: "tests",
      key: "tests",
      className: "CreateSubmissionInvoice__Details",
      render: (tests) => {
        if (tests == null || tests === "") return null;
        return (
          <StyledTooltip title={tests} placement="top">
            <span className="CreateSubmissionInvoice__Tests">{tests}</span>
          </StyledTooltip>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "11%",
      align: "right",
      render: (amount, record) => (
        <span className="CreateSubmissionInvoice__Amount">
          {formatInvoiceAmount(amount, record.currency || "USD")}
        </span>
      ),
    },
  ];

  // Selected submissions used to move to the top of the table.
  // const selectedKeySet = new Set(selectedRowKeys);
  // const tableRows = [
  //   ...selectedRowKeys.map((key) => selectedRowsById[key]).filter(Boolean),
  //   ...rows.filter((row) => !selectedKeySet.has(row.id)),
  // ];

  const updateSelectedSubmissions = (keys) => {
    if (keys.length > MAX_SUBMISSIONS_PER_INVOICE) {
      toast.error(`You can select up to ${MAX_SUBMISSIONS_PER_INVOICE} submissions per invoice`);
      return;
    }
    setSelectedRowKeys(keys);
    setSelectedRowsById((prev) => keys.reduce((next, key) => {
      const row = prev[key] || rows.find((item) => item.id === key);
      return row ? { ...next, [key]: row } : next;
    }, {}));
  };

  const toggleSubmissionSelection = (record) => {
    const isSelected = selectedRowKeys.includes(record.id);
    updateSelectedSubmissions(
      isSelected
        ? selectedRowKeys.filter((key) => key !== record.id)
        : [...selectedRowKeys, record.id],
    );
  };

  const selectedSubmissions = selectedRowKeys
    .map((key) => selectedRowsById[key])
    .filter(Boolean);
  const paneClass = (paneStep) => (
    step === paneStep ? "CreateSubmissionInvoice__StepPane" : "CreateSubmissionInvoice__StepPane is-hidden"
  );

  return (
    <div className="CreateSubmissionInvoice__StepHost">
      {detailsOpened && (
        <div className={paneClass("details")}>
          <SetInvoiceDetails
            onBack={() => setStep("select")}
            onPreview={(draft) => {
              setInvoiceDraft((current) => {
                if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
                return draft;
              });
              setStep("preview");
            }}
            submissions={selectedSubmissions}
          />
        </div>
      )}
      {step === "preview" && invoiceDraft && (
        <div className="CreateSubmissionInvoice__StepPane">
          <PreviewInvoice
            invoice={invoiceDraft}
            onBack={() => setStep("details")}
            onSaved={onSaved}
          />
        </div>
      )}
      <div className={paneClass("select")}>
        <div className="CreateSubmissionInvoice">
          <header className="CreateSubmissionInvoice__Header">
            <div className="CreateSubmissionInvoice__TitleBlock">
              <button type="button" className="CreateSubmissionInvoice__Back" onClick={onBack} aria-label="Back to invoices">
                <LeftOutlined />
              </button>
              <div>
                <h1 className="CreateSubmissionInvoice__Title">Create Submission Invoices</h1>
                <p className="CreateSubmissionInvoice__Description">
                  Select one or more submissions to include in a custom invoice.
                </p>
              </div>
            </div>
            <ol className="CreateSubmissionInvoice__Steps">
              {STEPS.map((label, index) => (
                <li key={label} className={index === 0 ? "is-active" : ""}>
                  <span className="CreateSubmissionInvoice__Step">
                    <span
                      className={`CreateSubmissionInvoice__StepIcon${index === 0 ? " is-active" : ""}`}
                      aria-hidden
                    >
                      {index + 1}
                    </span>
                    <span>{label}</span>
                  </span>
                  {index < STEPS.length - 1 && (
                    <RightOutlined className="CreateSubmissionInvoice__StepChevron" />
                  )}
                </li>
              ))}
            </ol>
          </header>

          <div className="CreateSubmissionInvoice__Controls">
            <Input
              type="search"
              size="large"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onPressEnter={() => applySearch(search)}
              suffix={(
                <Button
                  type="text"
                  icon={<SearchLineIcon aria-hidden />}
                  className="CreateSubmissionInvoice__SearchButton"
                  aria-label="Search submissions"
                  onClick={() => applySearch(search)}
                />
              )}
              placeholder="Search by submission or PO number"
              allowClear
              className="CreateSubmissionInvoice__Search"
            />
            <div className="CreateSubmissionInvoice__Filters">
              <Select
                className="CreateSubmissionInvoice__SubmittedBy"
                allowClear
                placeholder="Submitted by"
                value={submittedBy || undefined}
                options={submitters.map((name) => ({ value: name, label: name }))}
                {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                /* Keep ↓ after spread — AntD otherwise swaps to trailing search while open */
                suffixIcon={selectSearchSuffixIcon}
                classNames={selectPopupClassNames("CreateSubmissionInvoice__SubmittedByDropdown")}
                virtual={false}
                listHeight={256}
                popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                  optionCount: (submitters || []).length,
                })}
                onChange={(value) => {
                  setSubmittedBy(value || "");
                  resetTable();
                }}
              />
              <DatePicker
                handleSearchDate={(startDate, endDate) => {
                  setDateFrom(startDate);
                  setDateTo(endDate || startDate);
                  resetTable();
                }}
                value={dateSelected}
                onChange={setDateSelected}
                prevValue={prevDateSelected}
                setPrevValue={setPrevDateSelected}
                searchOnClear
                popoverPlacement="bottomRight"
                datePickerDisplayProps={{
                  displayClassName: `CreateSubmissionInvoice__DatePicker${
                    dateSelected ? " CreateSubmissionInvoice__DatePicker--active" : ""
                  }`,
                  showText: true,
                  iconTooltipTitle: "Filter by date",
                  emptyPlaceholder: "Select date range",
                  customCalendarIcon: (
                    <CalendarLineIcon className="CreateSubmissionInvoice__DateIcon" aria-hidden />
                  ),
                }}
              />
            </div>
          </div>

          <div className="CreateSubmissionInvoice__TableWrap">
            <StyledScrollbar
              ref={scrollbarRef}
              className="CreateSubmissionInvoice__ScrollBar"
              noScrollX
              disableTrackYWidthCompensation
              disableTracksWidthCompensation
              onScrollStop={handleScrollToBottom}
            >
              <Table
                className="CreateSubmissionInvoice__Table"
                rowKey="id"
                columns={columns}
                dataSource={loading ? [] : rows}
                pagination={false}
                locale={loading ? {
                  emptyText: <div className="CreateSubmissionInvoice__TableSkeleton ActiveloadingAnimation" />,
                } : undefined}
                rowSelection={{
                  selectedRowKeys,
                  preserveSelectedRowKeys: true,
                  onChange: updateSelectedSubmissions,
                  columnWidth: 48,
                  hideSelectAll: true,
                }}
                onRow={(record) => ({
                  onClick: (event) => {
                    if (event.target.closest(".ant-checkbox")) return;
                    toggleSubmissionSelection(record);
                  },
                })}
              />
              {loadingMore && !loading && (
                <div className="CreateSubmissionInvoice__LoadMore">
                  <Loading />
                </div>
              )}
            </StyledScrollbar>
          </div>

          <div className="CreateSubmissionInvoice__Footer">
            <Button
              type="primary"
              className="CreateSubmissionInvoice__Continue"
              disabled={selectedRowKeys.length === 0}
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              onClick={() => {
                setDetailsOpened(true);
                setStep("details");
              }}
            >
              {`Select Submissions (${selectedRowKeys.length})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
